/* eslint-disable no-plusplus */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-confusing-arrow */
import _cloneDeep from 'lodash/cloneDeep';
import _isEmpty from 'lodash/isEmpty';
import { getPxInInches, inchesToPxs } from '../../../../../utils/wall-size-utils';
import { useViewportStore } from '../../../../../store/ui';
import {
  DEFAULT_VIEWPORT_WALLSIZE,
  DEFAULT_TOUCH_POINT_WIDTH,
  WALL_SEGMENT_HEIGHT,
  BASE_CABINET_HEIGHT,
  CABINET_GAP_SPACE,
} from '../../../../../store/cabinetVisualizer/selectors';
import {
  isBaseProduct,
  isWallProduct,
  isPantryProduct,
  isTallWallProduct,
  isShortWallProduct
} from '../utils/cabinetLayout';

const standardHeight = BASE_CABINET_HEIGHT;
const standardWallCabinetHeight = WALL_SEGMENT_HEIGHT;
const countertopSpace = CABINET_GAP_SPACE;
const defaultZoom = 1;

const enableTouchPointsOnBothSides = true; // tryGetQueryParam('enableTouchPointsOnBothSides') === 'true';

export const getDefaultMinMaxColumns = (anchorSide) => {
  const offset = enableTouchPointsOnBothSides ? DEFAULT_TOUCH_POINT_WIDTH : 0;

  const defaultMinColumn = anchorSide === 'left' ? offset : 0;
  const defaultMaxColumn = DEFAULT_VIEWPORT_WALLSIZE.Columns - offset;

  return { defaultMinColumn, defaultMaxColumn };
};

export const getZoomedPxInInches = () => {
  const viewportInfo = useViewportStore.getState().viewportInfo;
  const pxInInches = getPxInInches(viewportInfo?.width
    ?? DEFAULT_VIEWPORT_WALLSIZE.Columns, DEFAULT_VIEWPORT_WALLSIZE.Columns);
  return (viewportInfo?.finalZoom ?? defaultZoom) * pxInInches;
};

export const originToScreen = (origin, pxInInches) => {
  if (!origin) return origin;
  const { Row, Column, Height, Width } = origin;

  if (pxInInches) {

    return {
      Row: inchesToPxs(Row, pxInInches),
      Column: inchesToPxs(Column, pxInInches),
      Width: inchesToPxs(Width, pxInInches),
      Height: inchesToPxs(Height, pxInInches)
    };
  }
  const zoomedPxInInches = getZoomedPxInInches();
  return {
    Row: inchesToPxs(Row, zoomedPxInInches),
    Column: inchesToPxs(Column, zoomedPxInInches),
    Width: inchesToPxs(Width, zoomedPxInInches),
    Height: inchesToPxs(Height, zoomedPxInInches)
  };
};

export const screenToOrigin = (screen, pxInInches) => {
  if (!screen || !pxInInches) return screen;
  const { Row, Column, Height, Width } = screen;

  return {
    Row: Number((Row / pxInInches).toFixed(2)),
    Column: Number((Column / pxInInches).toFixed(2)),
    Height: Number((Height / pxInInches).toFixed(2)),
    Width: Number((Width / pxInInches).toFixed(2))
  };
};

function isColliding(rectA, rectB, axis = 'both') {
  if (axis === 'horizontal') {
    return !(
      rectA.Column + rectA.Width <= rectB.Column
      || rectB.Column + rectB.Width <= rectA.Column
    );
  }

  if (axis === 'vertical') {
    if (rectA.Column >= rectB.Column + rectB.Width) {
      return false;
    }
    if (rectB.Column >= rectA.Column + rectA.Width) {
      return false;
    }
    return !(
      rectA.Row + rectA.Height <= rectB.Row
      || rectB.Row + rectB.Height <= rectA.Row
    );
  }

  // If both axes are considered
  return !(
    rectA.Column + rectA.Width <= rectB.Column
    || rectB.Column + rectB.Width <= rectA.Column
    || rectA.Row + rectA.Height <= rectB.Row
    || rectB.Row + rectB.Height <= rectA.Row
  );
}

export const isCabinetColliding = (productTypeA, rectA, cabinetB, axis, zoomedPxInInches) => {
  const productTypeB = cabinetB.productType;

  const rectB = cabinetB.Origin;
  let rectBInInches = cabinetB.Origin;

  if (zoomedPxInInches) {
    rectBInInches = screenToOrigin(rectBInInches, zoomedPxInInches);
  }
  let rectAInInches = { ...rectA };

  if (zoomedPxInInches) {
    rectAInInches = screenToOrigin(rectAInInches, zoomedPxInInches);
  }

  if (axis === 'vertical') {

    if (isWallProduct(productTypeA) && isBaseProduct(productTypeB)) {
      return isColliding(rectA, rectB, axis);
    }

    if (isBaseProduct(productTypeA) && isWallProduct(productTypeB)) {
      return isColliding(rectA, rectB, axis);
    }
    return false;
  }
  if (isWallProduct(productTypeA) && (isWallProduct(productTypeB)
    || isPantryProduct(productTypeB, rectBInInches.Height))) {
    return isColliding(rectA, rectB, axis);
  }
  if (isWallProduct(productTypeB) && (isWallProduct(productTypeA)
    || isPantryProduct(productTypeA, rectAInInches.Height))) {
    return isColliding(rectA, rectB, axis);
  }

  if (isBaseProduct(productTypeA) && isBaseProduct(productTypeB)) {
    return isColliding(rectA, rectB, axis);
  }

  return false;
};

export const isCollidedWithOtherProducts = (productType, origin, otherProducts, axis = 'both', zoomedPxInInches) => {
  const thisCabinetBox = {
    ...origin,
    Width: origin.Width,
    Height: origin.Height
  };

  const collision = otherProducts.some((product) => {
    return isCabinetColliding(productType, thisCabinetBox, product, axis, zoomedPxInInches);
  });

  return collision;
};

export const getShiftedProducts = (products, anchorSide) => {
  const { defaultMinColumn, defaultMaxColumn } = getDefaultMinMaxColumns(anchorSide);

  const minColumn = Math.min(...products.map((product) => product.Origin.Column));
  const maxColumn = Math.max(...products.map((product) => product.Origin.Column + product.Origin.Width));
  const needShift = (minColumn !== defaultMinColumn && anchorSide === 'left')
    || (maxColumn !== defaultMaxColumn && anchorSide === 'right');

  if (!needShift) {
    return products;
  }

  let shiftAmount = 0;
  if (minColumn !== defaultMinColumn && anchorSide === 'left') {
    shiftAmount = defaultMinColumn - minColumn;
  } else if (maxColumn !== defaultMaxColumn && anchorSide === 'right') {
    shiftAmount = defaultMaxColumn - maxColumn;
  }

  const updated = products.map((product) => {
    const newOrigin = { ...product.Origin };
    newOrigin.Column += shiftAmount;
    return { ...product, Origin: newOrigin };
  });

  return updated;
};
// filter products to only those that can collide or snap with activeCabinet
export const getFilteredProducts = (activeCabinet, products) => {
  const otherProducts = products.filter((product) => product.id !== activeCabinet.id);
  const isBase = isBaseProduct(activeCabinet.productType);
  // Filter products based on whether they are base or wall products
  // and whether they match the height criteria for pantry products.
  const baseProducts = otherProducts.filter((product) => isBaseProduct(product.productType));

  const wallProducts = otherProducts
    .filter((product) => !isBaseProduct(product.productType)
    || isPantryProduct(product.productType, product.Origin.Height));

  return isBase ? baseProducts : wallProducts;

};

export const isCornerCabinetWithVoidSpacer = (products, cabinet) => {
  const isCornerCabinet = cabinet.cornerCabinet;
  if (!isCornerCabinet) return false;

  const hasVoidSpacer = products.some((product) => {
    return product.id === cabinet.id && product.Sku === -3;
  });
  return hasVoidSpacer;
};
/**
 * Checks if a cabinet is dropped on a corner cabinet with a void spacer.
 * Returns true if dropped on the corner cabinet, the void spacer, or both.
 *
 * @param {Object} dropOrigin - The drop origin { Row, Column, Width, Height } in inches.
 * @param {Array} products - All cabinets on the wall.
 * @returns {boolean} True if dropped on corner cabinet or void spacer.
 */
export function isDropOnCornerCabinetWithVoidSpacer(dropOrigin, products) {
  // Find corner cabinets
  const cornerCabinets = products.filter((cab) => cab.cornerCabinet);

  // Exclude void spacers from other cabinets
  const otherCabinets = products.filter((cab) => !cab.cornerCabinet && cab.Sku !== -3);

  if (_isEmpty(cornerCabinets)) return false;

  // Check for collisions with corner cabinets and void spacers
  const collidedWithCorner = cornerCabinets.map((cab) => {
    const onCornerCabinet = isColliding(dropOrigin, cab.Origin);
    const voidSpacer = products.find((spacer) => spacer.id === cab.id && spacer.Sku === -3);
    const onVoidSpacer = voidSpacer ? isColliding(dropOrigin, voidSpacer.Origin) : false;

    return onCornerCabinet || onVoidSpacer;
  });

  // Check for collisions with other cabinets (excluding void spacers)
  const collidedWithOther = otherCabinets.map((cab) => {
    return isColliding(dropOrigin, cab.Origin);
  });

  // Return true if colliding with corner cabinet and/or its void spacer, but not with other cabinets
  return collidedWithCorner.includes(true) && !collidedWithOther.includes(true);
}

/**
 * Gets snap to cabinet position for corner cabinet
 * @param {Object} activeCornerCabinet - Active corner cabinet
 * @param {Object} newScreenOrigin - New screen origin
 * @param {Array} products - All products
 * @param {number} snapThresholdInInches - Snap threshold in pixels
 * @returns {Object|null} Snap position or null
 */
export const getSnapToCabinetPositionForCornerCabinet = (
  activeCornerCabinet, newScreenOrigin, products, snapThresholdInInches = 2) => {
  if (!activeCornerCabinet) return null;

  const zoomedPxInInches = getZoomedPxInInches();
  const snapThreshold = inchesToPxs(snapThresholdInInches, zoomedPxInInches);

  const filteredProducts = getFilteredProducts(activeCornerCabinet, products);
  if (filteredProducts.length === 0) return null; // No products to snap to

  const voidSpacer = products.find((cab) => cab.id === activeCornerCabinet.id && cab.Sku === -3);
  const voidSpacerOnRight = voidSpacer?.Origin.Column > activeCornerCabinet?.Origin.Column;

  const voidSpacerOrigin = originToScreen(voidSpacer?.Origin, zoomedPxInInches);

  let activeLeft = newScreenOrigin.Column;
  if (!voidSpacerOnRight && voidSpacerOrigin) {
    activeLeft -= voidSpacerOrigin.Width; // Adjust left edge if void spacer is on the left
  }
  const activeRight = activeLeft + newScreenOrigin.Width + (voidSpacerOrigin?.Width || 0);
  const activeTop = newScreenOrigin.Row;

  const otherProductsToScreenOrigin = filteredProducts.map((product) => {
    return originToScreen(product.Origin, zoomedPxInInches);
  });
  // Check if active cabinet is close to any other cabinet's left or right side
  if (otherProductsToScreenOrigin.length === 0) return null;
  // Find if within snapThreshold of any cabinet side
  const snappedProductOrigin = otherProductsToScreenOrigin.find((origin) => {
    const otherLeft = origin.Column;
    const otherRight = otherLeft + origin.Width;

    // Snap to left side
    if (Math.abs(activeLeft - otherRight) < snapThreshold) {
      return true;
    }
    // Snap to right side
    if (Math.abs(activeRight - otherLeft) < snapThreshold) {
      return true;
    }
    return false;
  });

  if (snappedProductOrigin) {
    const otherLeft = snappedProductOrigin.Column;
    const otherRight = otherLeft + snappedProductOrigin.Width;
    // Snap to right side
    if (Math.abs(activeLeft - otherRight) < snapThreshold) {
      return { posX: otherRight, posY: activeTop };
    }
    // Snap to left side
    if (Math.abs(activeRight - otherLeft) < snapThreshold) {
      return { posX: otherLeft - newScreenOrigin.Width - (voidSpacerOrigin?.Width || 0), posY: activeTop };
    }
  }
  return null;
};

/**
 * Gets snap to cabinet position
 * @param {Object} activeCabinet - Active cabinet
 * @param {Object} newScreenOrigin - New screen origin
 * @param {Array} products - All products
 * @param {number} snapThresholdInInches - Snap threshold in pixels
 * @returns {Object|null} Snap position or null
 */
export const getSnapToCabinetPosition = (activeCabinet, newScreenOrigin, products, snapThresholdInInches = 2) => {
  if (!activeCabinet) return null;

  const zoomedPxInInches = getZoomedPxInInches();
  const snapThreshold = inchesToPxs(snapThresholdInInches, zoomedPxInInches);

  if (isCornerCabinetWithVoidSpacer(products, activeCabinet)) {
    return getSnapToCabinetPositionForCornerCabinet(activeCabinet, newScreenOrigin, products, snapThreshold);
  }

  const filteredProducts = getFilteredProducts(activeCabinet, products);
  if (filteredProducts.length === 0) return null; // No products to snap to

  const activeLeft = newScreenOrigin.Column;
  const activeRight = activeLeft + newScreenOrigin.Width;
  const activeTop = newScreenOrigin.Row;

  const otherProductsToScreenOrigin = filteredProducts.map((product) => {
    return originToScreen(product.Origin, zoomedPxInInches);
  });
  // Check if active cabinet is close to any other cabinet's left or right side
  if (otherProductsToScreenOrigin.length === 0) return null;
  // Find if within snapThreshold of any cabinet side
  const snappedProductOrigin = otherProductsToScreenOrigin.find((origin) => {
    const otherLeft = origin.Column;
    const otherRight = otherLeft + origin.Width;

    // Snap to left side
    if (Math.abs(activeLeft - otherRight) < snapThreshold) {
      return true;
    }
    // Snap to right side
    if (Math.abs(activeRight - otherLeft) < snapThreshold) {
      return true;
    }
    return false;
  });

  if (snappedProductOrigin) {
    const otherLeft = snappedProductOrigin.Column;
    const otherRight = otherLeft + snappedProductOrigin.Width;
    // Snap to left side
    if (Math.abs(activeLeft - otherRight) < snapThreshold) {
      return { posX: otherRight, posY: activeTop };
    }
    // Snap to right side
    if (Math.abs(activeRight - otherLeft) < snapThreshold) {
      return { posX: otherLeft - newScreenOrigin.Width, posY: activeTop };
    }
  }
  return null;
};
